import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import {
  listSenders,
  createSender,
  updateSender,
  deleteSender,
  resetSenderCounter,
} from '../services/api/senderRegistryService';
import { listTenants } from '../services/api/tenantConfigService';

/**
 * SenderRegistryAdmin - Admin UI for managing email sender registrations
 * Allows admins to register/update/delete email senders for image uploads
 */
export default function SenderRegistryAdmin() {
  const [senders, setSenders] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [formData, setFormData] = useState({
    senderId: '',
    tenantId: '',
    emailAddress: '',
    dailyUploadLimit: 100,
    enabled: true,
    autoMatch: true,
  });

  useEffect(() => {
    loadSenders();
    loadTenants();
  }, []);

  const loadSenders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listSenders();
      setSenders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const data = await listTenants();
      setTenants(data);
    } catch (err) {
      console.error('Error loading tenants:', err);
      // Don't set error state - tenants list is optional
    }
  };

  const handleCreate = () => {
    setSelectedSender(null);
    setFormData({
      senderId: '',
      tenantId: '',
      emailAddress: '',
      dailyUploadLimit: 100,
      enabled: true,
      autoMatch: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (sender) => {
    setSelectedSender(sender);
    setFormData({
      senderId: sender.senderId,
      tenantId: sender.tenantId,
      emailAddress: sender.emailAddress,
      dailyUploadLimit: sender.dailyUploadLimit || 100,
      enabled: sender.enabled !== false,
      autoMatch: sender.autoMatch !== false,
    });
    setDialogOpen(true);
  };

  const handleDelete = (sender) => {
    setSelectedSender(sender);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Validate required fields for create
      if (!selectedSender) {
        if (!formData.senderId || !formData.senderId.trim()) {
          setError('Sender Email is required');
          return;
        }
        if (!formData.tenantId || !formData.tenantId.trim()) {
          setError('Tenant is required');
          return;
        }
        if (!formData.emailAddress || !formData.emailAddress.trim()) {
          setError('Recipient Email is required');
          return;
        }
      }

      if (selectedSender) {
        // Update existing
        await updateSender(selectedSender.senderId, {
          emailAddress: formData.emailAddress,
          dailyUploadLimit: formData.dailyUploadLimit,
          enabled: formData.enabled,
          autoMatch: formData.autoMatch,
        });
      } else {
        // Create new
        await createSender(formData);
      }
      setDialogOpen(false);
      await loadSenders();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setError(null);
      await deleteSender(selectedSender.senderId);
      setDeleteDialogOpen(false);
      await loadSenders();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetCounter = async (senderId) => {
    try {
      setError(null);
      await resetSenderCounter(senderId);
      await loadSenders();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Email Sender Registry</Typography>
        <Box>
          <IconButton
            onClick={loadSenders}
            title="Refresh"
            sx={{ mr: 1 }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={handleCreate}
            title="Register Sender"
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sender Email</TableCell>
                <TableCell>Recipient Email</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Uploads Today</TableCell>
                <TableCell>Daily Limit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Auto-Match</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {senders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No senders registered
                  </TableCell>
                </TableRow>
              ) : (
                senders.map((sender) => (
                  <TableRow key={sender.senderId}>
                    <TableCell>{sender.senderId}</TableCell>
                    <TableCell>{sender.emailAddress}</TableCell>
                    <TableCell>{sender.tenantId}</TableCell>
                    <TableCell>
                      {sender.uploadsToday || 0}
                      {sender.uploadsToday > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => handleResetCounter(sender.senderId)}
                          title="Reset Counter"
                          sx={{ ml: 1 }}
                        >
                          <RotateLeftIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>{sender.dailyUploadLimit || 'Unlimited'}</TableCell>
                    <TableCell>
                      <Chip
                        label={sender.enabled ? 'Enabled' : 'Disabled'}
                        color={sender.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sender.autoMatch ? 'Yes' : 'No'}
                        color={sender.autoMatch ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(sender)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(sender)}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedSender ? 'Edit Sender' : 'Register New Sender'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Sender Email"
              value={formData.senderId}
              onChange={(e) => setFormData({ ...formData, senderId: e.target.value })}
              disabled={!!selectedSender}
              fullWidth
              helperText="The email address that sends images (e.g., user@example.com)"
            />
            <TextField
              label="Recipient Email"
              value={formData.emailAddress}
              onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
              fullWidth
              helperText="The @cameras.kwari.com email to receive images"
            />
            <FormControl fullWidth disabled={!!selectedSender}>
              <InputLabel>Tenant</InputLabel>
              <Select
                value={formData.tenantId}
                onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                label="Tenant"
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.tenantName} ({tenant.tenantId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Daily Upload Limit"
              type="number"
              value={formData.dailyUploadLimit}
              onChange={(e) => setFormData({ ...formData, dailyUploadLimit: parseInt(e.target.value) })}
              fullWidth
              helperText="Maximum images per day (1-10000)"
              inputProps={{ min: 1, max: 10000 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                />
              }
              label="Enabled"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoMatch}
                  onChange={(e) => setFormData({ ...formData, autoMatch: e.target.checked })}
                />
              }
              label="Auto-Match Uploaded Images"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedSender ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete sender registration for{' '}
            <strong>{selectedSender?.senderId}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
